import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardPlusIcon, PlusIcon, SearchIcon, PackageIcon, CalendarIcon, TrashIcon, EyeIcon, XCircleIcon, CheckCircleIcon, AlertCircleIcon, BoxIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Textarea } from '../components/ui/Textarea';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { PageHeader } from '../components/ui/PageHeader';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useSortableData } from '../hooks/useSortableData';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { supabaseAdmin } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Purchase, PurchaseLine, Product } from '../types';
const SKU_PREFIX = 'PRD';
const ITEMS_PER_PAGE = 6;
interface CompraRow {
  id: string;
  numero_compra: string;
  fecha: string;
  registrado_por: string;
  registrado_por_id: string;
  total_items: number;
  total_cantidad: number;
  estado: 'completada' | 'anulada';
  notas: string;
  created_at: string;
}
const mapCompra = (row: CompraRow): Purchase => ({
  id: row.id,
  numeroCompra: row.numero_compra,
  fecha: new Date(row.fecha),
  registradoPor: row.registrado_por,
  registradoPorId: row.registrado_por_id,
  totalItems: row.total_items,
  totalCantidad: row.total_cantidad,
  estado: row.estado,
  notas: row.notas || '',
  lineas: [],
  createdAt: new Date(row.created_at)
});
const mapLineRow = (row: any): PurchaseLine => ({
  id: row.id,
  compraId: row.compra_id,
  productoId: row.producto_id,
  productoNombre: row.producto_nombre,
  productoSku: row.producto_sku,
  proveedor: row.proveedor,
  cantidad: row.cantidad,
  lote: row.lote || '',
  fechaEntrada: row.fecha_entrada || '',
  fechaVencimiento: row.fecha_vencimiento || ''
});
export function ComprasPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Data
  const [compras, setCompras] = useState<Purchase[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Create modal
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formNotas, setFormNotas] = useState('');
  const [formLines, setFormLines] = useState<PurchaseLine[]>([]);
  // Line entry
  const [selectedProductId, setSelectedProductId] = useState('');
  const [lineCantidad, setLineCantidad] = useState('1');
  const [lineLote, setLineLote] = useState('');
  const [lineFechaEntrada, setLineFechaEntrada] = useState(new Date().toISOString().split('T')[0]);
  const [lineFechaVencimiento, setLineFechaVencimiento] = useState('');
  const [lineProveedor, setLineProveedor] = useState('');
  // New product inline
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProdNombre, setNewProdNombre] = useState('');
  const [newProdPrecio, setNewProdPrecio] = useState('');
  const [newProdProveedor, setNewProdProveedor] = useState('');
  const [newProdCantidad, setNewProdCantidad] = useState('1');
  const [newProdLote, setNewProdLote] = useState('');
  const [newProdFechaEntrada, setNewProdFechaEntrada] = useState(new Date().toISOString().split('T')[0]);
  const [newProdFechaVencimiento, setNewProdFechaVencimiento] = useState('');
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  // View modal
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewCompra, setViewCompra] = useState<Purchase | null>(null);
  const [viewLines, setViewLines] = useState<PurchaseLine[]>([]);
  const [isLoadingLines, setIsLoadingLines] = useState(false);
  // Anular
  const [isAnulando, setIsAnulando] = useState(false);
  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchCompras = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('compras').select('*').order('created_at', {
        ascending: false
      }).limit(200);
      if (error) throw error;
      if (data) setCompras(data.map(mapCompra));
    } catch (err) {
      console.error('Error fetching compras:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);
  const fetchProducts = useCallback(async () => {
    try {
      const allProducts: Product[] = [];
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;
      while (hasMore) {
        const {
          data,
          error
        } = await supabaseAdmin.from('products').select('*').order('nombre').range(from, from + PAGE_SIZE - 1);
        if (error) {
          console.error('[ComprasPage] Error fetching products:', error);
          break;
        }
        if (data && data.length > 0) {
          allProducts.push(...data.map((row: any) => ({
            id: row.id,
            nombre: row.nombre,
            sku: row.sku,
            stock: row.stock,
            stockMinimo: row.stock_minimo ?? 0,
            precio: row.precio,
            subTotal: row.sub_total || 0,
            itbis: row.itbis || 0,
            proveedor: row.proveedor || ''
          })));
          from += PAGE_SIZE;
          hasMore = data.length === PAGE_SIZE;
        } else {
          hasMore = false;
        }
      }
      console.log('[ComprasPage] Products loaded:', allProducts.length);
      setProducts(allProducts);
    } catch (err) {
      console.error('[ComprasPage] Exception fetching products:', err);
    }
  }, []);
  useEffect(() => {
    fetchCompras();
    fetchProducts();
  }, [fetchCompras, fetchProducts]);
  useRealtimeRefresh(['compras'], fetchCompras);
  // ── Filtering ──────────────────────────────────────────────────────────────
  const filteredCompras = compras.filter(c => {
    if (searchQuery && !c.numeroCompra.toLowerCase().includes(searchQuery.toLowerCase()) && !c.registradoPor.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (dateFrom && new Date(c.fecha) < new Date(dateFrom)) return false;
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59);
      if (new Date(c.fecha) > to) return false;
    }
    return true;
  });
  const {
    items: sortedCompras,
    requestSort,
    sortConfig
  } = useSortableData(filteredCompras, {
    numeroCompra: c => c.numeroCompra,
    fecha: c => new Date(c.fecha).getTime(),
    registradoPor: c => c.registradoPor,
    totalItems: c => c.totalItems,
    totalCantidad: c => c.totalCantidad,
    estado: c => c.estado
  });
  const totalPages = Math.ceil(sortedCompras.length / ITEMS_PER_PAGE);
  const paginatedCompras = sortedCompras.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalCompras = compras.filter(c => c.estado === 'completada').length;
  const totalProductosIngresados = compras.filter(c => c.estado === 'completada').reduce((sum, c) => sum + c.totalCantidad, 0);
  const comprasHoy = compras.filter(c => {
    const today = new Date().toDateString();
    return new Date(c.fecha).toDateString() === today && c.estado === 'completada';
  }).length;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Intl.DateTimeFormat('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
  // ── Product options for SearchableSelect ────────────────────────────────────
  const productOptions = products.map(p => ({
    value: p.id,
    label: `${p.sku} — ${p.nombre}${p.proveedor ? ` (${p.proveedor})` : ''}`
  }));
  // ── Line management ────────────────────────────────────────────────────────
  const resetLineForm = () => {
    setSelectedProductId('');
    setLineCantidad('1');
    setLineLote('');
    setLineFechaEntrada(new Date().toISOString().split('T')[0]);
    setLineFechaVencimiento('');
    setLineProveedor('');
  };
  const handleProductSelect = (productId: string) => {
    setSelectedProductId(productId);
    const product = products.find(p => p.id === productId);
    if (product) {
      setLineProveedor(product.proveedor || '');
    }
  };
  const handleAddLine = () => {
    if (!selectedProductId || !lineCantidad || Number(lineCantidad) <= 0) return;
    const product = products.find(p => p.id === selectedProductId);
    if (!product) return;
    const newLine: PurchaseLine = {
      productoId: product.id,
      productoNombre: product.nombre,
      productoSku: product.sku,
      proveedor: lineProveedor || product.proveedor || '',
      cantidad: Number(lineCantidad),
      lote: lineLote,
      fechaEntrada: lineFechaEntrada,
      fechaVencimiento: lineFechaVencimiento
    };
    setFormLines(prev => [...prev, newLine]);
    resetLineForm();
  };
  const handleRemoveLine = (index: number) => {
    setFormLines(prev => prev.filter((_, i) => i !== index));
  };
  // ── New product creation ───────────────────────────────────────────────────
  const generateSku = () => {
    const existing = products.map(p => {
      const match = p.sku.match(new RegExp(`^${SKU_PREFIX}-(\\d+)$`, 'i'));
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${SKU_PREFIX}-${String(nextNum).padStart(3, '0')}`;
  };
  const handleCreateNewProduct = async () => {
    if (!newProdNombre || !newProdPrecio || Number(newProdCantidad) <= 0) return;
    setIsCreatingProduct(true);
    try {
      const sku = generateSku();
      const payload = {
        nombre: newProdNombre,
        sku,
        stock: 0,
        precio: Number(newProdPrecio),
        proveedor: newProdProveedor
      };
      const {
        data,
        error
      } = await supabaseAdmin.from('products').insert(payload).select().single();
      if (error) throw error;
      if (data) {
        const newProduct: Product = {
          id: data.id,
          nombre: data.nombre,
          sku: data.sku,
          stock: data.stock,
          stockMinimo: 0,
          precio: data.precio,
          proveedor: data.proveedor || ''
        };
        setProducts(prev => [...prev, newProduct].sort((a, b) => a.nombre.localeCompare(b.nombre)));
        // Add directly as a purchase line
        const newLine: PurchaseLine = {
          productoId: data.id,
          productoNombre: data.nombre,
          productoSku: data.sku,
          proveedor: data.proveedor || newProdProveedor,
          cantidad: Number(newProdCantidad),
          lote: newProdLote,
          fechaEntrada: newProdFechaEntrada,
          fechaVencimiento: newProdFechaVencimiento
        };
        setFormLines(prev => [...prev, newLine]);
        addActivityLog('Creó producto (desde Compras)', `Producto: ${newProdNombre} | SKU: ${sku}`);
      }
      setShowNewProduct(false);
      setNewProdNombre('');
      setNewProdPrecio('');
      setNewProdProveedor('');
      setNewProdCantidad('1');
      setNewProdLote('');
      setNewProdFechaEntrada(new Date().toISOString().split('T')[0]);
      setNewProdFechaVencimiento('');
    } catch (err) {
      console.error('Error creating product:', err);
      alert('Error al crear el producto.');
    } finally {
      setIsCreatingProduct(false);
    }
  };
  // ── Save purchase ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (formLines.length === 0) {
      alert('Agrega al menos un producto a la compra.');
      return;
    }
    setIsSaving(true);
    try {
      const totalItems = formLines.length;
      const totalCantidad = formLines.reduce((sum, l) => sum + l.cantidad, 0);
      const compraPayload = {
        numero_compra: '',
        fecha: new Date().toISOString(),
        registrado_por: user?.nombre || '',
        registrado_por_id: user?.id || null,
        total_items: totalItems,
        total_cantidad: totalCantidad,
        estado: 'completada',
        notas: formNotas
      };
      const {
        data: compraData,
        error: compraError
      } = await supabaseAdmin.from('compras').insert(compraPayload).select().single();
      if (compraError) throw compraError;
      const linesPayload = formLines.map(line => ({
        compra_id: compraData.id,
        producto_id: line.productoId,
        producto_nombre: line.productoNombre,
        producto_sku: line.productoSku,
        proveedor: line.proveedor,
        cantidad: line.cantidad,
        lote: line.lote,
        fecha_entrada: line.fechaEntrada || new Date().toISOString().split('T')[0],
        fecha_vencimiento: line.fechaVencimiento || null
      }));
      const {
        error: linesError
      } = await supabaseAdmin.from('compra_lines').insert(linesPayload);
      if (linesError) throw linesError;
      addActivityLog('Registró compra', `Compra: ${compraData.numero_compra} | ${totalItems} productos | ${totalCantidad} unidades`);
      await fetchCompras();
      await fetchProducts();
      handleCloseCreate();
    } catch (err) {
      console.error('Error saving compra:', err);
      alert('Error al guardar la compra. Intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  // ── View purchase ──────────────────────────────────────────────────────────
  const handleView = async (compra: Purchase) => {
    setViewCompra(compra);
    setIsViewOpen(true);
    setIsLoadingLines(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('compra_lines').select('*').eq('compra_id', compra.id).order('created_at');
      if (data) setViewLines(data.map(mapLineRow));
    } catch (err) {
      console.error('Error fetching lines:', err);
    } finally {
      setIsLoadingLines(false);
    }
  };
  // ── Anular ─────────────────────────────────────────────────────────────────
  const handleAnular = async () => {
    if (!viewCompra) return;
    if (!window.confirm('¿Está seguro que desea anular esta compra? Se revertirá el stock de los productos.')) return;
    setIsAnulando(true);
    try {
      // Delete lines first (trigger decrements stock)
      await supabaseAdmin.from('compra_lines').delete().eq('compra_id', viewCompra.id);
      // Update status
      const {
        error
      } = await supabaseAdmin.from('compras').update({
        estado: 'anulada',
        total_items: 0,
        total_cantidad: 0
      }).eq('id', viewCompra.id);
      if (error) throw error;
      addActivityLog('Anuló compra', `Compra: ${viewCompra.numeroCompra} anulada`);
      await fetchCompras();
      await fetchProducts();
      setIsViewOpen(false);
      setViewCompra(null);
    } catch (err) {
      console.error('Error anulando compra:', err);
      alert('Error al anular la compra.');
    } finally {
      setIsAnulando(false);
    }
  };
  // ── Modal helpers ──────────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setFormLines([]);
    setFormNotas('');
    resetLineForm();
    setShowNewProduct(false);
    setIsCreateOpen(true);
  };
  const handleCloseCreate = () => {
    setIsCreateOpen(false);
    setFormLines([]);
    setFormNotas('');
    resetLineForm();
    setShowNewProduct(false);
  };
  // ── Render ─────────────────────────────────────────────────────────────────
  return <div className="space-y-6" data-id="element-1411">
      <PageHeader title="Compras" subtitle="Ingreso de productos al inventario" icon={<ClipboardPlusIcon className="w-6 h-6" data-id="element-1413" />} data-id="element-1412">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1415" />} onClick={handleOpenCreate} data-id="element-1414">
          Nueva Compra
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 animate-stagger-1" data-id="element-1416">
        <StatsCard title="Total Compras" value={totalCompras} icon={<ClipboardPlusIcon className="w-5 h-5" data-id="element-1418" />} variant="primary" data-id="element-1417" />
        <StatsCard title="Productos Ingresados" value={totalProductosIngresados} subtitle="unidades totales" icon={<PackageIcon className="w-5 h-5" data-id="element-1420" />} variant="success" data-id="element-1419" />
        <StatsCard title="Compras Hoy" value={comprasHoy} icon={<CalendarIcon className="w-5 h-5" data-id="element-1422" />} variant="warning" data-id="element-1421" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 animate-stagger-2" data-id="element-1423">
        <div className="relative flex-1" data-id="element-1424">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1425" />
          <input type="text" placeholder="Buscar por número o usuario..." value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setCurrentPage(1);
        }} className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1426" />
        </div>
        <Input type="date" value={dateFrom} onChange={e => {
        setDateFrom(e.target.value);
        setCurrentPage(1);
      }} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1428" />} data-id="element-1427" />
        <Input type="date" value={dateTo} onChange={e => {
        setDateTo(e.target.value);
        setCurrentPage(1);
      }} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1430" />} data-id="element-1429" />
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-1431">
        {isLoading ? <div className="flex items-center justify-center py-16" data-id="element-1432">
            <div className="w-8 h-8 border-3 border-[#2952C4] border-t-transparent rounded-full animate-spin" data-id="element-1433" />
          </div> : paginatedCompras.length === 0 ? <div className="p-16 text-center" data-id="element-1434">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1435">
              <ClipboardPlusIcon className="w-8 h-8 text-slate-400" data-id="element-1436" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1437">
              No hay compras
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-1438">
              Registra tu primera compra para ingresar productos al inventario.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-1439">
            <table className="w-full text-sm" data-id="element-1440">
              <thead data-id="element-1441">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1442">
                  <SortableTableHeader label="No. Compra" sortKey="numeroCompra" sortConfig={sortConfig} onSort={requestSort} data-id="element-1443" />
                  <SortableTableHeader label="Fecha" sortKey="fecha" sortConfig={sortConfig} onSort={requestSort} data-id="element-1444" />
                  <SortableTableHeader label="Registrado por" sortKey="registradoPor" sortConfig={sortConfig} onSort={requestSort} data-id="element-1445" />
                  <SortableTableHeader label="Productos" sortKey="totalItems" sortConfig={sortConfig} onSort={requestSort} data-id="element-1446" />
                  <SortableTableHeader label="Cantidad" sortKey="totalCantidad" sortConfig={sortConfig} onSort={requestSort} data-id="element-1447" />
                  <SortableTableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} onSort={requestSort} data-id="element-1448" />
                  <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1449">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1450">
                {paginatedCompras.map(compra => <tr key={compra.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1451">
                    <td className="px-5 py-4 font-semibold text-[#2952C4]" data-id="element-1452">
                      {compra.numeroCompra}
                    </td>
                    <td className="px-5 py-4 text-slate-600" data-id="element-1453">
                      {formatDate(compra.fecha)}
                    </td>
                    <td className="px-5 py-4 text-slate-700" data-id="element-1454">
                      {compra.registradoPor}
                    </td>
                    <td className="px-5 py-4 text-slate-700 text-center" data-id="element-1455">
                      {compra.totalItems}
                    </td>
                    <td className="px-5 py-4 text-slate-700 text-center font-semibold" data-id="element-1456">
                      {compra.totalCantidad}
                    </td>
                    <td className="px-5 py-4" data-id="element-1457">
                      {compra.estado === 'completada' ? <Badge variant="success" data-id="element-1458">Completada</Badge> : <Badge variant="danger" data-id="element-1459">Anulada</Badge>}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-1460">
                      <Button variant="ghost" size="sm" onClick={() => handleView(compra)} data-id="element-1461">
                        <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-1462" />
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredCompras.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1463" />}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* CREATE MODAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal isOpen={isCreateOpen} onClose={handleCloseCreate} title="Nueva Compra" size="lg" data-id="element-1464">
        <ModalBody data-id="element-1465">
          <div className="space-y-6" data-id="element-1466">
            {/* Product selection */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4" data-id="element-1467">
              <div className="flex items-center justify-between" data-id="element-1468">
                <h4 className="text-sm font-bold text-slate-900" data-id="element-1469">
                  Agregar Producto
                  <span className="ml-2 text-xs font-normal text-slate-400" data-id="element-1470">
                    ({products.length} disponibles)
                  </span>
                </h4>
                <button type="button" onClick={() => setShowNewProduct(!showNewProduct)} className="text-xs font-semibold text-[#2952C4] hover:underline" data-id="element-1471">
                  {showNewProduct ? 'Cancelar' : '+ Producto Nuevo'}
                </button>
              </div>

              {/* New product inline form */}
              {showNewProduct && <div className="p-4 bg-white rounded-xl border border-blue-200 space-y-3" data-id="element-1472">
                  <p className="text-xs font-semibold text-[#2952C4]" data-id="element-1473">
                    Crear producto nuevo
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1474">
                    <Input label="Nombre" value={newProdNombre} onChange={e => setNewProdNombre(e.target.value)} placeholder="Nombre del producto" data-id="element-1475" />
                    <Input label="Precio" type="number" value={newProdPrecio} onChange={e => setNewProdPrecio(e.target.value)} placeholder="0.00" data-id="element-1476" />
                    <Input label="Proveedor" value={newProdProveedor} onChange={e => setNewProdProveedor(e.target.value)} placeholder="Nombre del proveedor" data-id="element-1477" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-1478">
                    <Input label="Cantidad" type="number" value={newProdCantidad} onChange={e => setNewProdCantidad(e.target.value)} placeholder="1" data-id="element-1479" />
                    <Input label="Lote" value={newProdLote} onChange={e => setNewProdLote(e.target.value)} placeholder="Ej: L-2025-01" data-id="element-1480" />
                    <Input label="Fecha Entrada" type="date" value={newProdFechaEntrada} onChange={e => setNewProdFechaEntrada(e.target.value)} data-id="element-1481" />
                    <Input label="Fecha Vencimiento" type="date" value={newProdFechaVencimiento} onChange={e => setNewProdFechaVencimiento(e.target.value)} data-id="element-1482" />
                  </div>
                  <Button size="sm" onClick={handleCreateNewProduct} isLoading={isCreatingProduct} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1484" />} disabled={!newProdNombre || !newProdPrecio || Number(newProdCantidad) <= 0} data-id="element-1483">
                    Crear y Agregar a la Compra
                  </Button>
                </div>}

              {/* Existing product select + line fields */}
              {!showNewProduct && <>
                  <SearchableSelect label="Producto" options={productOptions} value={selectedProductId} onChange={handleProductSelect} placeholder="Escribir para buscar producto..." maxVisible={10} data-id="element-1485" />

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-1486">
                    <Input label="Cantidad" type="number" value={lineCantidad} onChange={e => setLineCantidad(e.target.value)} placeholder="1" data-id="element-1487" />
                    <Input label="Lote" value={lineLote} onChange={e => setLineLote(e.target.value)} placeholder="Ej: L-2025-01" data-id="element-1488" />
                    <Input label="Fecha Entrada" type="date" value={lineFechaEntrada} onChange={e => setLineFechaEntrada(e.target.value)} data-id="element-1489" />
                    <Input label="Fecha Vencimiento" type="date" value={lineFechaVencimiento} onChange={e => setLineFechaVencimiento(e.target.value)} data-id="element-1490" />
                  </div>

                  <Input label="Proveedor" value={lineProveedor} onChange={e => setLineProveedor(e.target.value)} placeholder="Se auto-llena al seleccionar producto" data-id="element-1491" />

                  <Button size="sm" variant="secondary" onClick={handleAddLine} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1493" />} disabled={!selectedProductId || Number(lineCantidad) <= 0} data-id="element-1492">
                    Agregar a la Compra
                  </Button>
                </>}

              {/* Lines table */}
              {formLines.length > 0 && <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1494">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between" data-id="element-1495">
                    <h4 className="text-sm font-bold text-slate-900" data-id="element-1496">
                      Productos en esta compra ({formLines.length})
                    </h4>
                    <span className="text-xs font-semibold text-[#2952C4]" data-id="element-1497">
                      Total: {formLines.reduce((s, l) => s + l.cantidad, 0)}{' '}
                      unidades
                    </span>
                  </div>
                  <div className="overflow-x-auto" data-id="element-1498">
                    <table className="w-full text-sm" data-id="element-1499">
                      <thead data-id="element-1500">
                        <tr className="bg-slate-50 border-b border-slate-100" data-id="element-1501">
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1502">
                            Producto
                          </th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase" data-id="element-1503">
                            Cant.
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1504">
                            Lote
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1505">
                            Proveedor
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1506">
                            Vencimiento
                          </th>
                          <th className="px-4 py-2.5 w-10" data-id="element-1507" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-1508">
                        {formLines.map((line, idx) => <tr key={idx} className="hover:bg-slate-50/50" data-id="element-1509">
                            <td className="px-4 py-2.5" data-id="element-1510">
                              <p className="font-medium text-slate-900" data-id="element-1511">
                                {line.productoNombre}
                              </p>
                              <p className="text-xs text-slate-500" data-id="element-1512">
                                {line.productoSku}
                              </p>
                            </td>
                            <td className="px-4 py-2.5 text-center font-semibold text-slate-900" data-id="element-1513">
                              {line.cantidad}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600" data-id="element-1514">
                              {line.lote || '—'}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600" data-id="element-1515">
                              {line.proveedor || '—'}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600" data-id="element-1516">
                              {line.fechaVencimiento || '—'}
                            </td>
                            <td className="px-4 py-2.5" data-id="element-1517">
                              <button onClick={() => handleRemoveLine(idx)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1518">
                                <TrashIcon className="w-4 h-4" data-id="element-1519" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {formLines.length === 0 && <div className="text-center py-8 text-slate-400" data-id="element-1520">
                  <BoxIcon className="w-10 h-10 mx-auto mb-2 opacity-50" data-id="element-1521" />
                  <p className="text-sm" data-id="element-1522">
                    Agrega productos para crear la compra
                  </p>
                </div>}

              {/* Notas */}
              <Textarea label="Notas (opcional)" value={formNotas} onChange={e => setFormNotas(e.target.value)} rows={2} placeholder="Observaciones sobre esta compra..." data-id="element-1523" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1524">
          <Button variant="secondary" onClick={handleCloseCreate} data-id="element-1525">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={formLines.length === 0} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1527" />} data-id="element-1526">
            Guardar Compra ({formLines.length} productos)
          </Button>
        </ModalFooter>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* VIEW MODAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Modal isOpen={isViewOpen} onClose={() => {
      setIsViewOpen(false);
      setViewCompra(null);
      setViewLines([]);
    }} title={`Compra ${viewCompra?.numeroCompra || ''}`} size="lg" data-id="element-1528">
        <ModalBody data-id="element-1529">
          {viewCompra && <div className="space-y-6" data-id="element-1530">
              {/* Header info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-1531">
                <div data-id="element-1532">
                  <p className="text-xs text-slate-500" data-id="element-1533">Número</p>
                  <p className="font-bold text-slate-900" data-id="element-1534">
                    {viewCompra.numeroCompra}
                  </p>
                </div>
                <div data-id="element-1535">
                  <p className="text-xs text-slate-500" data-id="element-1536">Fecha</p>
                  <p className="font-semibold text-slate-700" data-id="element-1537">
                    {formatDate(viewCompra.fecha)}
                  </p>
                </div>
                <div data-id="element-1538">
                  <p className="text-xs text-slate-500" data-id="element-1539">Registrado por</p>
                  <p className="font-semibold text-slate-700" data-id="element-1540">
                    {viewCompra.registradoPor}
                  </p>
                </div>
                <div data-id="element-1541">
                  <p className="text-xs text-slate-500" data-id="element-1542">Estado</p>
                  {viewCompra.estado === 'completada' ? <Badge variant="success" data-id="element-1543">Completada</Badge> : <Badge variant="danger" data-id="element-1544">Anulada</Badge>}
                </div>
              </div>

              {viewCompra.notas && <div className="p-3 bg-slate-50 rounded-xl border border-slate-200" data-id="element-1545">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-1546">Notas</p>
                  <p className="text-sm text-slate-700" data-id="element-1547">{viewCompra.notas}</p>
                </div>}

              {/* Lines */}
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1548">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200" data-id="element-1549">
                  <h4 className="text-sm font-bold text-slate-900" data-id="element-1550">
                    Productos ({viewCompra.totalItems}) —{' '}
                    {viewCompra.totalCantidad} unidades
                  </h4>
                </div>
                {isLoadingLines ? <div className="flex items-center justify-center py-8" data-id="element-1551">
                    <div className="w-6 h-6 border-2 border-[#2952C4] border-t-transparent rounded-full animate-spin" data-id="element-1552" />
                  </div> : viewLines.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm" data-id="element-1553">
                    {viewCompra.estado === 'anulada' ? 'Compra anulada — productos revertidos' : 'Sin líneas'}
                  </div> : <div className="overflow-x-auto" data-id="element-1554">
                    <table className="w-full text-sm" data-id="element-1555">
                      <thead data-id="element-1556">
                        <tr className="bg-slate-50 border-b border-slate-100" data-id="element-1557">
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1558">
                            Producto
                          </th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase" data-id="element-1559">
                            Cant.
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1560">
                            Lote
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1561">
                            Proveedor
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1562">
                            Entrada
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1563">
                            Vencimiento
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-1564">
                        {viewLines.map(line => <tr key={line.id} data-id="element-1565">
                            <td className="px-4 py-2.5" data-id="element-1566">
                              <p className="font-medium text-slate-900" data-id="element-1567">
                                {line.productoNombre}
                              </p>
                              <p className="text-xs text-slate-500" data-id="element-1568">
                                {line.productoSku}
                              </p>
                            </td>
                            <td className="px-4 py-2.5 text-center font-semibold text-slate-900" data-id="element-1569">
                              {line.cantidad}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600" data-id="element-1570">
                              {line.lote || '—'}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600" data-id="element-1571">
                              {line.proveedor || '—'}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600" data-id="element-1572">
                              {line.fechaEntrada || '—'}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600" data-id="element-1573">
                              {line.fechaVencimiento || '—'}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-1574">
          <div className="flex items-center justify-between w-full" data-id="element-1575">
            <div data-id="element-1576">
              {viewCompra?.estado === 'completada' && isAdmin && <Button variant="danger" onClick={handleAnular} isLoading={isAnulando} leftIcon={<XCircleIcon className="w-4 h-4" data-id="element-1578" />} data-id="element-1577">
                  Anular Compra
                </Button>}
            </div>
            <Button variant="secondary" onClick={() => {
            setIsViewOpen(false);
            setViewCompra(null);
            setViewLines([]);
          }} data-id="element-1579">
              Cerrar
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>;
}